import { useState, useCallback, useEffect } from 'react';

/**
 * useFormFields — Detects and manages PDF form fields.
 *
 * Scans all pages for Widget annotations (text, checkbox, radio, dropdown,
 * signature). Returns field data, current values, and control functions.
 * Field positions use PDF coordinate space — the overlay component converts
 * to screen coordinates using the viewport transform.
 */
export default function useFormFields(pdfDoc) {
  const [fields, setFields] = useState([]);
  const [values, setValues] = useState({});
  const [highlightFields, setHighlightFields] = useState(true);
  const [detecting, setDetecting] = useState(false);

  // Detect form fields when PDF loads
  useEffect(() => {
    if (!pdfDoc) {
      setFields([]);
      setValues({});
      return;
    }

    let cancelled = false;
    setDetecting(true);

    (async () => {
      const detected = [];
      let fieldId = 0;

      for (let i = 1; i <= pdfDoc.numPages; i++) {
        if (cancelled) return;
        const page = await pdfDoc.getPage(i);
        let annotations;
        try {
          annotations = await page.getAnnotations();
        } catch (_) {
          continue;
        }

        // We only treat GENUINE AcroForm /Widget fields as interactive form
        // fields. Everything else in the PDF — including FreeText annotations
        // (filled OR empty), stamps, ink, etc. — is rendered faithfully by
        // PDF.js onto the page canvas via its own /AP appearance streams,
        // exactly as stored. We do NOT detect, synthesize, or re-render
        // non-widget annotations. (A "fillable" PDF built from empty FreeText
        // boxes, like SmallPDF's, opens as-is; the user adds values with the
        // Text/Sign tools, which save as clean annotations.)
        for (const annot of annotations) {
          if (annot.subtype !== 'Widget') continue;
          if (!annot.rect || annot.rect.length < 4) continue;

          const base = {
            id: `ff-${++fieldId}`,
            pageNum: i,
            source: 'widget',
            name: annot.fieldName || `unnamed-${fieldId}`,
            rect: annot.rect,
            readOnly: !!annot.readOnly,
            required: !!(annot.required || (annot.fieldFlags & 2)),
            // PDF.js's internal annotation id (e.g. "722R") — matches
            // data-element-id on the input PDF.js renders inside its
            // .textWidgetAnnotation section. Used at save time to DOM-scrape
            // typed values back into fieldData (PDF.js writes to its own
            // annotationStorage and the input DOM directly; React's
            // formValues store doesn't see the keystrokes).
            annotId: annot.id,
          };

          if (annot.fieldType === 'Tx') {
            detected.push({
              ...base,
              type: 'text',
              multiline: !!annot.multiLine,
              defaultValue: annot.fieldValue || '',
              maxLen: annot.maxLen || 0,
            });
          } else if (annot.fieldType === 'Btn') {
            if (annot.checkBox) {
              detected.push({
                ...base,
                type: 'checkbox',
                exportValue: annot.exportValue || 'Yes',
                defaultValue: annot.fieldValue === annot.exportValue,
              });
            } else if (annot.radioButton) {
              detected.push({
                ...base,
                type: 'radio',
                radioGroup: annot.fieldName || '',
                exportValue: annot.buttonValue || annot.exportValue || '',
                defaultValue:
                  annot.fieldValue === (annot.buttonValue || annot.exportValue),
              });
            }
            // Push buttons are skipped
          } else if (annot.fieldType === 'Ch') {
            const options = (annot.options || []).map((o) =>
              typeof o === 'string' ? o : o.displayValue || o.exportValue || ''
            );
            detected.push({
              ...base,
              type: 'dropdown',
              options,
              defaultValue: annot.fieldValue || '',
            });
          } else if (annot.fieldType === 'Sig') {
            detected.push({
              ...base,
              type: 'signature',
              defaultValue: '',
            });
          }
        }
        // NOTE: no FreeText/marker "form field" fallback. FreeText boxes (the
        // way SmallPDF and similar tools build "fillable" forms) are rendered
        // faithfully on the page canvas by PDF.js — never promoted to synthetic
        // form fields, never re-rendered through a DOM overlay. That overlay
        // path diverted appearance streams off the canvas and mangled already-
        // filled forms; removing it is what restores source-of-truth rendering.
      }

      if (cancelled) return;

      setFields(detected);

      // Initialize values from defaults
      const initialValues = {};
      for (const f of detected) {
        initialValues[f.id] =
          f.defaultValue ?? (f.type === 'checkbox' || f.type === 'radio' ? false : '');
      }
      setValues(initialValues);
      setDetecting(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [pdfDoc]);

  const setValue = useCallback((fieldId, value) => {
    setValues((prev) => {
      const next = { ...prev, [fieldId]: value };
      const changed = fields.find((field) => field.id === fieldId);
      if (changed && (changed.type === 'radio' || changed.type === 'checkbox')) {
        for (const field of fields) {
          if (field.id === fieldId || field.name !== changed.name || field.type !== changed.type) continue;
          if (field.exportValue === changed.exportValue) next[field.id] = value;
          else if (value) next[field.id] = false;
        }
      }
      return next;
    });
  }, [fields]);

  const clearAll = useCallback(() => {
    const cleared = {};
    for (const f of fields) {
      cleared[f.id] = f.type === 'checkbox' || f.type === 'radio' ? false : '';
    }
    setValues(cleared);
  }, [fields]);

  return {
    fields,
    values,
    setValue,
    clearAll,
    highlightFields,
    setHighlightFields,
    detecting,
    hasFields: fields.length > 0,
    fieldCount: fields.length,
  };
}
